import {Type} from 'class-transformer';
import {BaseCollectionModel, FileUploadRes} from '@yasc/yasc-material-components';
import {BankAccountRes} from './bank.contract';
import {CompanyDetailsMinRes} from './company.contract';
import {UserDetailsMinRes} from './user.contract';
import {OrderAmountUnit} from '../../../enums/OrderAmountUnit';
import {InvoiceDocumentFormat} from '../../../enums/invoice-document-format';
import {ProductDetailMinRes} from './product.contract';

export class InvoiceAddressRes {
    id!: number;
    companyName = '';
    street = '';
    postalCode = '';
    city = '';
    country = '';
    vatId = '';
}

export class PositionDetailRes {
    id!: number;
    description = '';
    amount = 0;
    priceNetto = 0;
    taxPercent = 19;
    totalBrutto = 0;
    totalNetto = 0;
    totalMwst = 0;
    unit: OrderAmountUnit = OrderAmountUnit.generalPieces;
    sourceWorkTimeEntryIds: number[] = [];
    projectBudgetId?: number | null;
}

export class PositionCreateReq {
    price = 0;
    amount = 1;
    taxPercent = 19;
    orderAmountUnit: OrderAmountUnit = OrderAmountUnit.generalPieces;
    description = '';
    sourceWorkTimeEntryIds: number[] = [];
    projectBudgetId?: number | null;

    static FromDetails(value: Partial<PositionDetailRes>): PositionCreateReq {
        return Object.assign(new PositionCreateReq(), {
            price: value.priceNetto ?? 0,
            amount: value.amount ?? 1,
            taxPercent: value.taxPercent ?? 19,
            orderAmountUnit: value.unit ?? OrderAmountUnit.generalPieces,
            description: value.description ?? '',
            sourceWorkTimeEntryIds: [...(value.sourceWorkTimeEntryIds ?? [])],
            projectBudgetId: value.projectBudgetId ?? null,
        });
    }
}

export class InvoiceDetailRes {
    id!: number;
    idNumber = 0;
    idRevisionNumber = 0;
    version = 1;
    invoiceNumber = '';
    status = 'draft';
    invoiceDate!: string;
    billingPeriodStart!: string;
    billingPeriodEnd!: string;
    deliveryDate?: string | null;
    paidOn?: string | null;
    paidAmount = 0;
    paymentTarget!: string;
    sentDate?: string | null;
    finalizedAt?: string | null;
    cancelledAt?: string | null;
    @Type(() => ProductDetailMinRes)
    product?: ProductDetailMinRes | null;
    totalBrutto = 0;
    totalMwst = 0;
    totalNetto = 0;
    created!: string;
    projectId?: number | null;
    projectName = '';
    sourceOfferId?: number | null;
    sourceOfferNumber = '';
    paymentAddressId?: number | null;
    subject = '';
    orderNumber = '';
    customerReference = '';
    introText = '';
    closingText = '';
    internalNote = '';
    paymentTermsDays = 30;
    paymentTermsText = '';
    paymentDiscountPercent?: number | null;
    paymentDiscountDays?: number | null;
    billingBasis = 'manual';
    hourlyRate = 0;
    onlyVerifiedTimes = false;
    selectedTaskIds: string[] = [];
    selectedMemberIds: number[] = [];
    selectedProjectBudgetIds: number[] = [];
    preferredDocumentFormat: InvoiceDocumentFormat = InvoiceDocumentFormat.XRechnungXml;
    isImported = false;
    importedFormat?: InvoiceDocumentFormat | null;
    importedAt?: string | null;
    originalDocument?: FileUploadRes | null;
    attachment?: FileUploadRes | null;
    bankAccount?: BankAccountRes | null;
    company?: CompanyDetailsMinRes | null;
    companyContact?: UserDetailsMinRes | null;
    sellerCompany?: CompanyDetailsMinRes | null;
    sellerContact?: UserDetailsMinRes | null;
    paymentAddress?: InvoiceAddressRes | null;
    sellerAddress?: InvoiceAddressRes | null;

    @Type(() => PositionDetailRes)
    positions: PositionDetailRes[] = [];
}

export class InvoiceCreateReq {
    invoiceDate?: Date;
    paidOn?: Date | null;
    paidAmount?: number;
    paymentTarget!: Date;
    billingPeriodStart!: Date;
    billingPeriodEnd!: Date;
    deliveryDate?: Date | null;
    buyerContactId!: number;
    companyId!: number;
    paymentAddressId!: number;
    sellerContactId?: number | null;
    bankAccountId?: string | null;
    productId?: string | null;
    projectId?: number | null;
    sourceOfferId?: number | null;
    subject = '';
    orderNumber = '';
    customerReference = '';
    introText = '';
    closingText = '';
    internalNote = '';
    paymentTermsDays = 30;
    paymentTermsText = '';
    paymentDiscountPercent?: number | null;
    paymentDiscountDays?: number | null;
    billingBasis = 'manual';
    hourlyRate = 0;
    onlyVerifiedTimes = false;
    selectedTaskIds: string[] = [];
    selectedMemberIds: number[] = [];
    selectedProjectBudgetIds: number[] = [];
    preferredDocumentFormat: InvoiceDocumentFormat = InvoiceDocumentFormat.XRechnungXml;
    positions: PositionCreateReq[] = [];

    static FromDetails(value: Partial<InvoiceDetailRes>): InvoiceCreateReq {
        const dto = new InvoiceCreateReq();
        dto.invoiceDate = value.invoiceDate ? new Date(value.invoiceDate) : undefined;
        dto.paidOn = value.paidOn == null ? null : new Date(value.paidOn);
        dto.paidAmount = value.paidAmount ?? 0;
        dto.paymentTarget = value.paymentTarget ? new Date(value.paymentTarget) : new Date();
        dto.billingPeriodStart = value.billingPeriodStart ? new Date(value.billingPeriodStart) : new Date();
        dto.billingPeriodEnd = value.billingPeriodEnd ? new Date(value.billingPeriodEnd) : new Date();
        dto.deliveryDate = value.deliveryDate == null ? null : new Date(value.deliveryDate);
        dto.buyerContactId = value.companyContact?.id ?? 0;
        dto.companyId = value.company?.id ?? 0;
        dto.paymentAddressId = value.paymentAddressId ?? value.paymentAddress?.id ?? 0;
        dto.sellerContactId = value.sellerContact?.id ?? null;
        dto.bankAccountId = value.bankAccount?.guid ?? null;
        dto.productId = undefined;
        dto.projectId = value.projectId ?? null;
        dto.sourceOfferId = value.sourceOfferId ?? null;
        dto.subject = value.subject ?? '';
        dto.orderNumber = value.orderNumber ?? '';
        dto.customerReference = value.customerReference ?? '';
        dto.introText = value.introText ?? '';
        dto.closingText = value.closingText ?? '';
        dto.internalNote = value.internalNote ?? '';
        dto.paymentTermsDays = value.paymentTermsDays ?? 30;
        dto.paymentTermsText = value.paymentTermsText ?? '';
        dto.paymentDiscountPercent = value.paymentDiscountPercent ?? null;
        dto.paymentDiscountDays = value.paymentDiscountDays ?? null;
        dto.billingBasis = value.billingBasis ?? 'manual';
        dto.hourlyRate = value.hourlyRate ?? 0;
        dto.onlyVerifiedTimes = value.onlyVerifiedTimes ?? false;
        dto.selectedTaskIds = [...(value.selectedTaskIds ?? [])];
        dto.selectedMemberIds = [...(value.selectedMemberIds ?? [])];
        dto.selectedProjectBudgetIds = [...(value.selectedProjectBudgetIds ?? [])];
        dto.preferredDocumentFormat = value.preferredDocumentFormat ?? InvoiceDocumentFormat.XRechnungXml;
        dto.positions = (value.positions ?? []).map(position => PositionCreateReq.FromDetails(position));
        return dto;
    }
}

export class InvoiceUpdateReq {
    buyerContactId?: number;
    companyId?: number;
    paymentAddressId?: number;
    sellerContactId?: number | null;
    bankAccountId?: string | null;
    projectId?: number | null;
    sourceOfferId?: number | null;
    invoiceDate?: Date;
    paymentTarget?: Date;
    billingPeriodStart?: Date;
    billingPeriodEnd?: Date;
    deliveryDate?: Date | null;
    subject?: string;
    orderNumber?: string;
    customerReference?: string;
    introText?: string;
    closingText?: string;
    internalNote?: string;
    paymentTermsDays?: number;
    paymentTermsText?: string;
    paymentDiscountPercent?: number | null;
    paymentDiscountDays?: number | null;
    billingBasis?: string;
    hourlyRate?: number;
    onlyVerifiedTimes?: boolean;
    selectedTaskIds?: string[];
    selectedMemberIds?: number[];
    selectedProjectBudgetIds?: number[];
    preferredDocumentFormat?: InvoiceDocumentFormat;
    positions?: PositionCreateReq[];
    paidOn?: Date | null;
    paidAmount?: number;
    version?: number;
}


export class BillingDocumentFinalizePreviewRes {
    documentNumber = '';
}

export class BillingDocumentFinalizeReq {
    documentNumber = '';
    previewPngBase64 = '';
}

export class InvoicePaidReq {
    paidOn: Date = new Date();
    amount?: number;
}

export class InvoiceProjectSourceReq {
    projectId!: number;
    fromUtc!: Date;
    toUtc!: Date;
    groupBy: 'project' | 'task' | 'member' | 'entry' = 'project';
    onlyVerified = false;
    hourlyRate = 0;
    taxPercent = 19;
    taskIds: string[] = [];
    memberIds: number[] = [];
    projectBudgetIds: number[] = [];
    revisionOfIdNumber?: number | null;
}

export class ProjectBudgetSourceRes {
    id!: number;
    name = '';
    userId?: number | null;
    userName = '';
    startDate!: string;
    endDate?: string | null;
    totalHours = 0;
    usedHours = 0;
    remainingHours = 0;
    hourlyRate = 0;
    orderNumber = '';
    sourceOfferId?: number | null;
    sourceOfferNumber = '';
}

export class InvoiceProjectSourceRes {
    projectId!: number;
    projectName = '';
    customerId?: number | null;
    customerName = '';
    projectVolumeBrutto = 0;
    projectVolumeBruttoOpen = 0;
    actualWorkedHours = 0;
    workTimeEntryCount = 0;
    excludedUnverifiedEntryCount = 0;
    alreadyBilledEntryCount = 0;
    alreadyBilledHours = 0;
    selectedBudgetHours = 0;
    selectedBudgetRemainingHours = 0;
    budgetOverflowHours = 0;
    @Type(() => ProjectBudgetSourceRes)
    budgets: ProjectBudgetSourceRes[] = [];
    @Type(() => PositionCreateReq)
    positions: PositionCreateReq[] = [];
}

export class InvoiceSendReq {
    receivers: string[] = [];
    formats: InvoiceDocumentFormat[] = [InvoiceDocumentFormat.Pdf, InvoiceDocumentFormat.XRechnungXml];
}

export interface PayInvoiceReq {
    invoiceId: number;
    paidOn: string;
}

export type InvoiceCollectionRes = BaseCollectionModel<InvoiceDetailRes>;
